import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EcQuartermapSetupService } from './ec-quartermap-setup.service';

@Component({
    selector: 'gtw-ec-quartermap',
    templateUrl: './ec-quartermap-setup.component.html',
    styleUrls: ['./ec-quartermap-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EcQuartermapSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  minimise = false;

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  headerData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Component properties
  quarterMapForm: UntypedFormGroup;
  crudLoading = false;
  deleteLoading = false;
  showEntityLoader = false;
  isEditMode = false;
  modalCallbackName = '';
  modalTitle = "EC QuarterMap";
  showFromYearPicker = false;
  
  // Year picker functionality
  availableYears: number[] = [];
  
  // Data arrays
  allscenarioTypeCode: any[] = [];
  allscenarioDesc: any[] = [];
  allGroupObjByYear: any[] = [];
  allFilingCycle: any[] = [];

  // Form entity
  quarterMapEntity: any = {
    GROUP_OBJ_KEY: "",
    TAX_YEAR: "",
    RPT_MTH: "",
    TAX_YEAR_END: "",
    FILING_CYCLE: "",
    FROM_TAX_YEAR: "",
    FROM_RPT_MTH: "",
    TAX_YEAR_BEGIN: ""
  };

  constructor(
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private gtwSnackbarService: GtwSnackbarService,
    private ecQuartermapSetupService: EcQuartermapSetupService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.quarterMapForm = this.fb.group({
      TAX_YEAR: ['', Validators.required],
      SCENARIO_TYPE_CODE: ['', Validators.required],
      SCENARIO_DESC: ['', Validators.required],
      GROUP_OBJ_KEY: ['', Validators.required],
      RPT_MTH: ['', Validators.required],
      TAX_YEAR_END: ['', Validators.required],
      FILING_CYCLE: ['', Validators.required],
      FROM_TAX_YEAR: ['', Validators.required],
      FROM_RPT_MTH: ['', Validators.required],
      TAX_YEAR_BEGIN: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.generateAvailableYears();
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.headerData = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.modalCallbackName =
      activeScreen?.data?.modalName
      || activeScreen?.data?.modal_name
      || activeScreen?.data?.colData?.call_back
      || '';
    this.rowData = this.resolveQuarterMapRow(activeScreen);
    this.isEditMode = this.resolveIsEditMode();
    this.modalTitle = this.isEditMode ? 'EC Configuration' : 'EC QuarterMap';
    this.cdr.markForCheck();
  }

  /** Create is createQuarterMapEntity; edit is updateQuarterMapEntity. */
  private resolveIsEditMode(): boolean {
    const callback = String(this.modalCallbackName || '').toLowerCase();
    if (callback.includes('create')) {
      return false;
    }
    if (callback.includes('update')) {
      return true;
    }
    return this.rowData?.GROUP_OBJ_KEY != null && this.rowData.GROUP_OBJ_KEY !== '';
  }

  /**
   * Legacy update reads gridData.rowData. Batch actions wrap the row as { data }.
   */
  private resolveQuarterMapRow(activeScreen: any): any {
    const activeData = activeScreen?.data || {};
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    const candidates = [
      gridPayload?.rowData,
      activeData?.rowData,
      activeData?.selected?.rowData,
      rowPayloadFromActiveScreen(activeScreen)
    ];

    for (const candidate of candidates) {
      const row = this.unwrapQuarterMapRow(candidate);
      if (this.looksLikeQuarterMapRow(row)) {
        return row;
      }
    }

    return this.unwrapQuarterMapRow(candidates.find((item: any) => item != null) ?? {});
  }

  private unwrapQuarterMapRow(raw: any): any {
    if (Array.isArray(raw)) {
      const first = raw[0];
      if (first?.data && typeof first.data === 'object') {
        return first.data;
      }
      return first && typeof first === 'object' ? first : {};
    }
    if (raw?.data && typeof raw.data === 'object' && this.looksLikeQuarterMapRow(raw.data)) {
      return raw.data;
    }
    return raw && typeof raw === 'object' ? raw : {};
  }

  private looksLikeQuarterMapRow(row: any): boolean {
    if (!row || Array.isArray(row) || typeof row !== 'object') {
      return false;
    }
    return (row.GROUP_OBJ_KEY != null && row.GROUP_OBJ_KEY !== '')
      || (row.FILING_CYCLE != null && row.FILING_CYCLE !== '')
      || (row.RPT_MTH != null && row.RPT_MTH !== '')
      || (row.TAX_YEAR != null && row.TAX_YEAR !== '')
      || (row.tax_year != null && row.tax_year !== '');
  }

  private resolveTaxYear(row: any): string {
    const direct = row?.TAX_YEAR ?? row?.tax_year ?? row?.TAXYEAR;
    if (direct != null && direct !== '') {
      return this.displayYear(direct);
    }

    const headers = this.gtwWorkspace.workspaceFactory?.activeScreen?.data?.header;
    if (Array.isArray(headers)) {
      const taxYearHeader = headers.find((header: any) =>
        String(header?.name || header?.display_name || '').trim().toLowerCase() === 'tax year'
      );
      const mapped = taxYearHeader?.map ? row?.[taxYearHeader.map] : undefined;
      if (mapped != null && mapped !== '') {
        return this.displayYear(mapped);
      }
    }

    const filterYear = this.filterParams?.tax_year ?? this.gtwGlobal.global?.globalParams?.tax_year;
    return filterYear != null && filterYear !== '' ? this.displayYear(filterYear) : '';
  }

  private displayYear(value: any): string {
    if (value == null || value === '') {
      return '';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return String(value.getFullYear());
    }
    const text = String(value).trim();
    const yearMatch = text.match(/\d{4}/);
    return yearMatch ? yearMatch[0] : text;
  }

  init(): void {
    if (this.isEditMode) {
      this.populateFormForEdit();
    } else {
      this.populateFormForCreate();
    }

    if (this.filterParams && Object.keys(this.filterParams).length > 0) {
      this.loadInitialData();
    } else {
      this.showEntityLoader = false;
      this.cdr.markForCheck();
    }
  }

  loadInitialData(): void {
    this.showEntityLoader = true;

    this.loadScenarioTypeCode();
    this.loadDistFilingCycle();

    this.showEntityLoader = false;
    this.cdr.markForCheck();
  }

  populateFormForEdit(): void {
    const taxYear = this.resolveTaxYear(this.rowData);
    this.quarterMapEntity = {
      GROUP_OBJ_KEY: this.rowData.GROUP_OBJ_KEY ?? "",
      TAX_YEAR: taxYear,
      RPT_MTH: this.rowData.RPT_MTH ?? "",
      TAX_YEAR_END: this.resolveDisplayDate(this.rowData, ['TAX_YEAR_END', 'tax_year_end'], ['tax year end']),
      FILING_CYCLE: this.rowData.FILING_CYCLE ?? "",
      FROM_TAX_YEAR: this.displayYear(this.rowData.FROM_TAX_YEAR),
      FROM_RPT_MTH: this.rowData.FROM_RPT_MTH ?? "",
      TAX_YEAR_BEGIN: this.resolveDisplayDate(this.rowData, ['TAX_YEAR_BEGIN', 'tax_year_begin'], ['tax year begin']),
      SCENARIO_TYPE_CODE: this.rowData.SCENARIO_TYPE_CODE ?? "",
      SCENARIO_DESC: this.rowData.SCENARIO_DESC ?? ""
    };

    ['SCENARIO_TYPE_CODE', 'SCENARIO_DESC'].forEach(name => {
      const control = this.quarterMapForm.get(name);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
    this.quarterMapForm.patchValue(this.quarterMapEntity);
    this.cdr.markForCheck();
  }

  populateFormForCreate(): void {
    this.quarterMapEntity.TAX_YEAR = this.resolveTaxYear(this.rowData);
    
    // Convert dates to HTML5 date input format (YYYY-MM-DD)
    this.quarterMapEntity.TAX_YEAR_BEGIN = this.convertToDateInputFormat(`01/01/${this.quarterMapEntity.TAX_YEAR}`);
    this.quarterMapEntity.TAX_YEAR_END = this.convertToDateInputFormat(`12/31/${this.quarterMapEntity.TAX_YEAR}`);

    // Update form controls
    this.quarterMapForm.patchValue(this.quarterMapEntity);
  }

  loadScenarioTypeCode(): void {
    this.ecQuartermapSetupService.loadScenarioTypeCode(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (scenarioTypeCode: any[]) => {
        console.log("load all Scenario Type Code", scenarioTypeCode);
        this.allscenarioTypeCode = scenarioTypeCode;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading scenario type codes:", error);
        this.gtwSnackbarService.openSnackBar('Error loading scenario type codes', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadScenarioDesc(selectedValue: string): void {
    this.ecQuartermapSetupService.loadScenarioDesc(this.GENERAL_CONFIG.base_url, selectedValue, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (scenarioDesc: any[]) => {
        console.log("load all Scenario Desc", scenarioDesc);
        this.allscenarioDesc = scenarioDesc;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading scenario descriptions:", error);
        this.gtwSnackbarService.openSnackBar('Error loading scenario descriptions', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadGroupObjKey(selectedVal1: string, selectedVal2: string): void {
    this.ecQuartermapSetupService.loadGroupObjKey(this.GENERAL_CONFIG.base_url, selectedVal1, selectedVal2, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (groupObjByYear: any[]) => {
        console.log("load all Group Obj", groupObjByYear);
        this.allGroupObjByYear = groupObjByYear;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading group object keys:", error);
        this.gtwSnackbarService.openSnackBar('Error loading group object keys', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadDistFilingCycle(): void {
    this.ecQuartermapSetupService.loadDistFilingCycle(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (filingCycle: any[]) => {
        console.log("load all Filing Cycle", filingCycle);
        this.allFilingCycle = filingCycle;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading filing cycles:", error);
        this.gtwSnackbarService.openSnackBar('Error loading filing cycles', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  onScenarioTypeCodeChange(): void {
    const scenarioTypeCode = this.quarterMapForm.get('SCENARIO_TYPE_CODE')?.value;
    console.log(scenarioTypeCode);
    this.loadScenarioDesc(scenarioTypeCode);
    this.quarterMapForm.get('GROUP_OBJ_KEY')?.setValue('');
  }

  onPopulateGroupObjYear(): void {
    const scenarioTypeCode = this.quarterMapForm.get('SCENARIO_TYPE_CODE')?.value;
    const scenarioDesc = this.quarterMapForm.get('SCENARIO_DESC')?.value;
    console.log(scenarioTypeCode + '............' + scenarioDesc);
    this.loadGroupObjKey(scenarioTypeCode, scenarioDesc);
  }

  isValid(): boolean {
    const form = this.quarterMapForm;
    if (!form.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach(key => {
        form.get(key)?.markAsTouched();
      });
      return false;
    }
    return true;
  }

  save(): void {
    this.persistQuarterMap('EC Quarter Map created successfully');
  }

  update(): void {
    this.persistQuarterMap('EC Quarter Map updated successfully');
  }

  private persistQuarterMap(successMessage: string): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    this.crudLoading = true;
    const saveData = this.buildSaveData();

    this.ecQuartermapSetupService.saveQuarterMapForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(successMessage, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  delete(): void {
    const groupObjKey = this.quarterMapForm.get('GROUP_OBJ_KEY')?.value || this.rowData?.GROUP_OBJ_KEY;
    const filingCycle = this.quarterMapForm.get('FILING_CYCLE')?.value || this.rowData?.FILING_CYCLE;
    const rptMth = this.quarterMapForm.get('RPT_MTH')?.value || this.rowData?.RPT_MTH;
    if (!groupObjKey || !filingCycle || rptMth == null || rptMth === '') {
      this.gtwSnackbarService.openSnackBar('Missing required data for deletion', 'gtw-snackbar--red');
      return;
    }

    this.deleteLoading = true;
    
    this.ecQuartermapSetupService.deleteQuarterMap(
      this.GENERAL_CONFIG.base_url,
      groupObjKey,
      filingCycle,
      rptMth,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.deleteLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Quarter Map deleted successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.deleteLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  buildSaveData(): any {
    let saveData = _.merge({}, this.quarterMapForm.value);
    
    // FROM_TAX_YEAR is now a number input, no conversion needed
    
    if (saveData.TAX_YEAR_END) {
      saveData.TAX_YEAR_END = this.convertFromDateInputFormat(saveData.TAX_YEAR_END);
    }
    
    if (saveData.TAX_YEAR_BEGIN) {
      saveData.TAX_YEAR_BEGIN = this.convertFromDateInputFormat(saveData.TAX_YEAR_BEGIN);
    }
    
    return saveData;
  }

  formatDate(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // Convert a grid or filter date to YYYY-MM-DD for HTML5 date inputs
  convertToDateInputFormat(date: any): string {
    return this.toIsoDate(date);
  }

  /**
   * Edit mode shows MM/DD/YYYY text, matching the legacy datepicker display.
   * A readonly input type="date" stays blank for any value that is not YYYY-MM-DD.
   */
  private resolveDisplayDate(row: any, propertyNames: string[], headerLabels: string[]): string {
    for (const name of propertyNames) {
      const formatted = this.formatUsDate(row?.[name]);
      if (formatted) {
        return formatted;
      }
    }

    const headers = this.gtwWorkspace.workspaceFactory?.activeScreen?.data?.header;
    if (!Array.isArray(headers)) {
      return '';
    }
    const wanted = headerLabels.map(label => label.toLowerCase());
    const header = headers.find((item: any) =>
      wanted.includes(String(item?.name || item?.display_name || '').trim().toLowerCase())
    );
    return this.formatUsDate(header?.map ? row?.[header.map] : undefined);
  }

  private formatUsDate(value: any): string {
    const iso = this.toIsoDate(value);
    if (!iso) {
      return '';
    }
    const [year, month, day] = iso.split('-');
    return `${month}/${day}/${year}`;
  }

  private toIsoDate(value: any): string {
    if (value == null || value === '') {
      return '';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return this.isoFromParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
    }
    if (typeof value === 'number' && isFinite(value)) {
      const parsed = new Date(value);
      return isNaN(parsed.getTime())
        ? ''
        : this.isoFromParts(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
    }

    const text = String(value).trim();
    const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usDate) {
      return this.isoFromParts(Number(usDate[3]), Number(usDate[1]), Number(usDate[2]));
    }
    const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDate) {
      return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`;
    }
    return '';
  }

  private isoFromParts(year: number, month: number, day: number): string {
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // Convert YYYY-MM-DD back to MM/DD/YYYY for API calls
  convertFromDateInputFormat(dateString: string): string {
    if (!dateString) return '';
    
    // If in YYYY-MM-DD format, convert to MM/DD/YYYY
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    
    // If already in MM/DD/YYYY format, return as is
    return dateString;
  }

  reset(): void {
    const taxYear = this.filterParams.tax_year || this.rowData.TAX_YEAR;
    
    // Convert dates to HTML5 date input format (YYYY-MM-DD)
    const taxYearBegin = this.convertToDateInputFormat(`01/01/${taxYear}`);
    const taxYearEnd = this.convertToDateInputFormat(`12/31/${taxYear}`);
    
    this.quarterMapForm.reset();
    this.quarterMapForm.patchValue({
      TAX_YEAR: taxYear,
      TAX_YEAR_BEGIN: taxYearBegin,
      TAX_YEAR_END: taxYearEnd
    });
  }

  toggleMinimize(): void {
    this.minimise = !this.minimise;
  }

  generateAvailableYears(): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 10; // 10 years back
    const endYear = currentYear + 10;   // 10 years forward
    
    this.availableYears = [];
    for (let year = startYear; year <= endYear; year++) {
      this.availableYears.push(year);
    }
  }

  get fromTaxYearNumber(): number | null {
    const year = parseInt(this.quarterMapForm?.get('FROM_TAX_YEAR')?.value, 10);
    return isNaN(year) ? null : year;
  }

  openFromYearPicker(): void {
    this.showFromYearPicker = !this.showFromYearPicker;
    this.cdr.markForCheck();
  }

  closeFromYearPicker(): void {
    this.showFromYearPicker = false;
    this.cdr.markForCheck();
  }

  onFromYearSelected(year: number | null): void {
    this.quarterMapForm.get('FROM_TAX_YEAR')?.setValue(year == null ? '' : String(year));
    this.quarterMapForm.get('FROM_TAX_YEAR')?.markAsDirty();
    this.showFromYearPicker = false;
    this.cdr.markForCheck();
  }

  openNativeDatePicker(input: HTMLInputElement, currentValue: string): void {
    const iso = this.toIsoDate(currentValue);
    if (iso) {
      input.value = iso;
    }
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.focus();
      input.click();
    }
  }

  onCalendarDateSelected(controlName: string, event: Event): void {
    const iso = (event.target as HTMLInputElement).value;
    this.quarterMapForm.get(controlName)?.setValue(this.formatUsDate(iso));
    this.quarterMapForm.get(controlName)?.markAsDirty();
    this.cdr.markForCheck();
  }

  onFromTaxYearChange(): void {
    const fromTaxYear = this.quarterMapForm.get('FROM_TAX_YEAR')?.value;
    const taxYear = this.quarterMapForm.get('TAX_YEAR')?.value;
    
    // Validate that From Tax Year is not greater than Tax Year
    if (fromTaxYear && taxYear && parseInt(fromTaxYear) > parseInt(taxYear)) {
      this.gtwSnackbarService.openSnackBar('From Tax Year cannot be greater than Tax Year', 'gtw-snackbar--red');
      this.quarterMapForm.get('FROM_TAX_YEAR')?.setValue('');
    }
  }

  cancel(): void {
    // AngularJS handles modal close automatically via event listener
    // this.requestGridUpdate.emit('refresh');
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-ec-quartermap-ce')) {
      document.querySelector('gtw-ec-quartermap-ce')?.dispatchEvent(updateEvent);
    }
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  toggleDatePicker(field: string): void {
    // Placeholder method for date picker functionality
    // This can be implemented later if needed
    console.log('Date picker clicked for field:', field);
  }
}
